import { mergeAttributes, Node } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import IframeEmbed from './IframeEmbed.js'

export interface IframeOptions {
  allowFullscreen: boolean
  HTMLAttributes: {
    [key: string]: any
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe: {
      /**
       * Add an iframe
       */
      setIframe: (options?: { src: string }) => ReturnType
      setHtml: (value: string) => ReturnType
      insertIframe: (src?: string, className?: string) => ReturnType
    }
  }
}

export const Iframe = Node.create<IframeOptions>({
  name: 'iframe',
  inline: false,
  group: 'block',
  content: 'inline*',
  draggable: true,

  atom: true,

  addOptions() {
    return {
      allowFullscreen: true,
      HTMLAttributes: {
        class: 'iframe-wrapper',
      },
    }
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
      frameborder: {
        default: 0,
      },
      allowfullscreen: {
        default: this.options.allowFullscreen,
        parseHTML: () => this.options.allowFullscreen,
      },
      sandbox: {
        default: 'allow-scripts allow-same-origin allow-forms allow-popups allow-presentation',
      },
      referrerpolicy: {
        default: 'no-referrer-when-downgrade',
      },
      width: {
        default: '100%',
      },
      height: {
        default: 'auto',
      },
      title: {
        default: null,
      },
      loading: {
        default: 'lazy',
      },
    }
  },
  renderHTML({ HTMLAttributes }) {
    return ['iframe', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
  },
  parseHTML() {
    return [
      {
        tag: 'iframe',
      },
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(IframeEmbed)
  },

  addCommands() {
    return {
      setIframe:
        (options: { src: string }) =>
        ({ tr, dispatch }) => {
          const { selection } = tr
          let attributes: Record<string, any> = {
            ...options,
          }

          if (options?.src?.includes('iframe')) {
            const parser = new DOMParser()
            const html = parser.parseFromString(options?.src, 'text/html')
            const attrNames = html.getElementsByTagName('iframe')?.[0]?.getAttributeNames()

            attrNames?.forEach((attr) => {
              attributes[attr] = html.getElementsByTagName('iframe')?.[0]?.getAttribute(attr)
            })
          } else if (!isValidHttpUrl(options?.src)) {
            const htmlData = 'data:text/html;charset=utf-8,' + encodeURI(options?.src)
            attributes = {
              ...attributes,
              src: htmlData,
            }
          }
          const node = this.type.create(attributes)

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node)
          }

          // return commands.insertContent({
          //   type: 'iframe',
          //   attrs: attributes,
          // })

          return true
        },

      setHtml:
        (value) =>
        ({ commands, state }) => {
          console.log(value)
          let attributes: Record<string, any> = {
            editorValue: value,
          }

          if (value?.includes('youtube.com')) {
            const ytValue = value?.split('v=')?.[1]?.split('&')?.[0]
            attributes = {
              ...attributes,

              src: `https://www.youtube.com/embed/${ytValue}?controls=0`,
              title: 'YouTube video player',
            }
          } else if (value?.includes('instagram.com')) {
            const instagramValue = value?.split('p/')?.[1]?.split('/')?.[0]
            attributes = {
              ...attributes,

              src: `https://www.instagram.com/p/${instagramValue}/embed`,
              title: 'Instagram',
            }
          } else if (isValidHttpUrl(value)) {
            attributes = {
              ...attributes,

              src: value,
            }
          } else if (value?.includes('iframe')) {
            const parser = new DOMParser()
            const html = parser.parseFromString(value, 'text/html')
            const attrNames = html.getElementsByTagName('iframe')?.[0]?.getAttributeNames()

            attrNames?.forEach((attr) => {
              attributes[attr] = html.getElementsByTagName('iframe')?.[0]?.getAttribute(attr)
            })
          } else {
            const htmlData = 'data:text/html;charset=utf-8,' + encodeURI(value)
            attributes = {
              ...attributes,
              src: htmlData,
            }
          }

          return true
        },
      insertIframe:
        (src, className) =>
        ({ commands, state }) => {
          return commands.insertContent({
            type: 'iframe',
            attrs: {
              src,
              class: className,
            },
          })
        },
    }
  },
})

function isValidHttpUrl(string: string) {
  let url

  try {
    url = new URL(string)
  } catch (_) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}
